<template>
    <div class="ui-page">
        <ui-page-header title="Credenciamentos" subtitle="Solicitações de credenciamento dos credores para as assembleias.">
            <v-btn color="primary" depressed :loading="dataList.exportLoading" @click="load(true)">
                <v-icon left small>mdi-microsoft-excel</v-icon>
                Exportar planilha
            </v-btn>
        </ui-page-header>

        <div class="ui-list-bar">
            <ui-pills v-model="dataList.filter.status" :items="statusPills" @input="applyFilters" />
        </div>

        <ui-table-card clearable :can-clear="hasFilters" @clear="clearFilters">
            <template #toolbar>
                <v-text-field v-model="dataList.filter.name" placeholder="Buscar pelo nome do credor" prepend-inner-icon="mdi-magnify"
                    dense outlined hide-details class="ui-toolbar__search" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.registration" placeholder="CPF/CNPJ" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-text-field v-model="dataList.filter.email" placeholder="E-mail" dense outlined hide-details
                    class="ui-toolbar__field" @input="filterDebounced" @keyup.enter="applyFilters"></v-text-field>
                <v-select v-model="dataList.filter.client" :items="dataset.clients" item-value="id" item-text="name"
                    placeholder="Todos os casos" dense outlined hide-details class="ui-toolbar__field"
                    :menu-props="{ offsetY: true }" @change="applyFilters"></v-select>
            </template>

            <v-data-table :headers="dataList.headers" :items="dataList.data" :server-items-length="dataList.count"
                :options.sync="dataList.options" :loading="dataList.loading" disable-sort hide-default-footer
                no-data-text="Nenhum credenciamento foi encontrado" loading-text="Carregando dados..." class="ui-clickable-rows"
                @click:row="viewItem">
                <template v-slot:[`item.creditor`]="{ item }">
                    <div class="ui-cell-main">{{ getCreditorName(item) }}</div>
                    <div class="ui-cell-sub">{{ getCreditorEmail(item) }}</div>
                </template>

                <template v-slot:[`item.case`]="{ item }">
                    <v-tooltip bottom max-width="520">
                        <template #activator="{ on, attrs }">
                            <span v-bind="attrs" v-on="on" class="case-name-ellipsis">
                                {{ getClientName(item) }}
                            </span>
                        </template>
                        <span>{{ getClientName(item) }}</span>
                    </v-tooltip>
                </template>

                <template v-slot:[`item.generalMeeting`]="{ item }">
                    <div>{{ getGeneralMeetingDate(item) }}</div>
                    <div class="ui-cell-sub" :title="getGeneralMeetingName(item)">
                        {{ getGeneralMeetingName(item) }}
                    </div>
                </template>

                <template v-slot:[`item.participation_type`]="{ item }">
                    {{ getParticipationTypeLabel(item.participation_type) }}
                </template>

                <template v-slot:[`item.created_at`]="{ item }">
                    {{ item.created_at | defaultDate("DD/MM/YYYY HH:mm [BRT]") }}
                </template>

                <template v-slot:[`item.analyzed_at`]="{ item }">
                    <span v-if="item.analyzed_at && item.analyzed_at !== '0000-00-00 00:00:00'">
                        {{ item.analyzed_at | defaultDate("DD/MM/YYYY HH:mm [BRT]") }}
                    </span>
                    <span v-else>N/A</span>
                </template>

                <template v-slot:[`item.admin`]="{ item }">
                    {{ item.admin && item.admin.name ? item.admin.name : 'N/A' }}
                </template>

                <template v-slot:[`item.status`]="{ item }">
                    <ui-badge :color="getColorByStatus(item.status)">{{ item.status | enum(dataset.accreditationStatus, "id", "name") }}</ui-badge>
                </template>

                <template v-slot:[`item.actions`]="{ item }">
                    <ui-row-actions :actions="[
                        { text: 'Analisar credenciamento', icon: 'mdi-clipboard-search-outline', handler: () => viewItem(item) },
                        { text: 'Excluir credenciamento', icon: 'mdi-delete-outline', danger: true, handler: () => openSingleDeletion(item) , disabled: !$allowed('delete') },
                    ]" />
                </template>
            </v-data-table>

            <template #footer>
                <ui-pagination :total="dataList.count" :page="dataList.options.page" :items-per-page="dataList.options.itemsPerPage"
                    :disabled="dataList.loading" @update:page="pageChanged" @update:itemsPerPage="setPageSize" />
            </template>
        </ui-table-card>

        <accreditation-details-modal ref="accreditationDetailsModalRef"></accreditation-details-modal>
        <accreditation-delete-dialog v-model="singleDeletion.show" :item="singleDeletion.item" @deleted="load"></accreditation-delete-dialog>
    </div>
</template>

<script>
import { downloadXlsx } from "@/common/util/export-xlsx.util";
import axios from 'axios';
import _ from 'lodash';

import AccreditationDetailsModal from "@/components/AccreditationDetailsModal.vue";
import AccreditationDeleteDialog from "@/components/AccreditationDeleteDialog.vue";
import accreditationStatus from "@/common/data/accreditation-status.data";

export default {
    name: "AccreditationList",

    components: {
        'accreditation-details-modal': AccreditationDetailsModal,
        'accreditation-delete-dialog': AccreditationDeleteDialog,
    },

    data: () => ({
        breadcrumbItems: [
            { text: 'Homepage', disabled: false, to: '/home', exact: true },
            { text: 'Credenciamentos', disabled: true, exact: true },
        ],
        // Exclusao de um credenciamento pelo icone da linha, com motivo em editor.
        singleDeletion: {
            show: false,
            item: null,
        },
        dataset: {
            clients: [{ id: "", name: "Todos" }],
            statuses: [{ id: "", name: "Todos" }, ...accreditationStatus],
            accreditationStatus,
        },
        dataList: {
            filter: {
                name: "",
                registration: "",
                email: "",
                client: "",
                status: "",
            },
            headers: [
                { text: "Credor", value: "creditor" },
                { text: "Caso", value: "case" },
                { text: "AGC", value: "generalMeeting" },
                { text: "Participação", value: "participation_type" },
                { text: "Solicitado em", value: "created_at" },
                { text: "Analisado por", value: "admin" },
                { text: "Analisado em", value: "analyzed_at" },
                { text: "Status", value: "status" },
                { text: '', value: 'actions', sortable: false, align: 'end', width: 56 },
            ],
            data: [],
            loading: false,
            exportLoading: false,
            count: 0,
            options: {
                page: 1,
                itemsPerPage: 10,
                sortBy: ['created_at'],
                sortDesc: [true],
            },
            footerOptions: {
                itemsPerPageText: 'Itens por página:',
                itemsPerPageOptions: [10, 20, 30, 40, 50],
            },
        },
    }),

    computed: {
        hasFilters() {
            return Object.values(this.dataList.filter).some((value) => value || value === 0);
        },

        statusPills() {
            const tones = { 0: "warning", 1: "success", 2: "danger", 3: "purple" };
            return this.dataset.statuses.map((item) => ({
                value: item.id,
                text: item.name,
                tone: item.id === "" ? undefined : tones[item.id],
                count: this.dataList.filter.status === item.id ? this.dataList.count : undefined,
            }));
        },
    },

    created() {
        this.load();
        this.loadClients();
    },

    methods: {
        openSingleDeletion(item) {
            this.singleDeletion.item = item;
            this.singleDeletion.show = true;
        },

        applyFilters() {
            this.dataList.options.page = 1;
            this.load();
        },

        filterDebounced: _.debounce(function () {
            this.applyFilters();
        }, 400),

        // A troca de tamanho e seguida de update:page(1), que recarrega.
        setPageSize(size) {
            this.dataList.options.itemsPerPage = size;
        },

        pageChanged(page) {
            this.dataList.options.page = page;
            this.load();
        },

        pageSizeChanged(size) {
            this.dataList.options.itemsPerPage = size;
            this.load();
        },

        clearFilters() {
            this.dataList.filter = {
                name: "",
                registration: "",
                email: "",
                client: "",
                status: "",
            };
            this.dataList.options.page = 1;
            this.load();
        },

        buildSearch() {
            const search = {};

            Object.entries(this.dataList.filter).forEach(([key, value]) => {
                if (value || value === 0) search[key] = value;
            });

            return Object.keys(search).length ? search : null;
        },

        async load(exportCsv = false) {
            const limit = exportCsv ? 1000000 : (this.dataList.options.itemsPerPage || 10);
            const page = exportCsv ? 1 : this.dataList.options.page;
            const search = this.buildSearch();

            if (exportCsv) this.dataList.exportLoading = true;
            else this.dataList.loading = true;

            try {
                const result = await axios.get('/api/admin/accreditations', {
                    params: { page, limit, search },
                });

                const content = result.data.content || {};

                if (exportCsv) {
                    this.generateCSV(content.data || []);
                    return;
                }

                this.dataList.data = content.data || [];
                this.dataList.count = content.paging ? content.paging.total : 0;
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 5000 });
            } finally {
                this.dataList.loading = false;
                this.dataList.exportLoading = false;
            }
        },

        async loadClients() {
            try {
                const result = await axios.get(`/api/clients`, { params: { start: 1, limit: 100000, search: {} } });
                const content = result.data.content;
                const clients = Array.isArray(content) ? content : (content.data || []);
                this.dataset.clients = [{ id: "", name: "Todos" }, ...clients];
            } catch (error) {
                this.$dialog.notify.error(this.$codes(error), { position: 'top-right', timeout: 2000 });
            }
        },

        getColorByStatus(status) {
            switch (Number(status)) {
                case 0:
                    return 'warning';
                case 1:
                    return 'accent';
                case 2:
                    return 'error';
                case 3:
                    return 'purple';
                default:
                    return 'grey';
            }
        },

        getCreditorName(item) {
            return item.accreditation && item.accreditation.user ? item.accreditation.user.name : 'N/A';
        },

        getCreditorEmail(item) {
            return item.accreditation && item.accreditation.user ? item.accreditation.user.email : 'N/A';
        },

        getClientName(item) {
            const accreditationClient = item.accreditation && item.accreditation.client ? item.accreditation.client.name : null;
            const generalMeetingClient = item.generalMeeting && item.generalMeeting.client ? item.generalMeeting.client.name : null;
            return accreditationClient || generalMeetingClient || 'N/A';
        },

        getGeneralMeetingName(item) {
            return item.generalMeeting && item.generalMeeting.name ? item.generalMeeting.name : 'N/A';
        },

        getGeneralMeetingDate(item) {
            return item.generalMeeting && item.generalMeeting.date
                ? this.$moment(item.generalMeeting.date).format('DD/MM/YYYY HH:mm')
                : 'N/A';
        },

        getParticipationTypeLabel(participationType) {
            return Number(participationType) === 2 ? 'Ouvinte' : 'Credor';
        },

        viewItem(item) {
            if (!this.$refs.accreditationDetailsModalRef) return;

            this.$refs.accreditationDetailsModalRef.setData(item);
            this.$refs.accreditationDetailsModalRef.open().then(() => {
                this.load();
            });
        },

        async generateCSV(data) {
            if (!data || !data.length) {
                this.$dialog.notify.error('Nenhum credenciamento encontrado para exportar.', { position: 'top-right', timeout: 2000 });
                return;
            }

            const rows = data.map(item => ({
                "NOME CREDOR": this.getCreditorName(item),
                "EMAIL CREDOR": this.getCreditorEmail(item),
                "CASO": this.getClientName(item),
                "AGC": this.getGeneralMeetingName(item),
                "DATA AGC": this.getGeneralMeetingDate(item),
                "PARTICIPACAO": this.getParticipationTypeLabel(item.participation_type),
                "SOLICITADO EM": item.created_at,
                "ANALISADO POR": item.admin && item.admin.name ? item.admin.name : 'N/A',
                "ANALISADO EM": item.analyzed_at || 'N/A',
                "STATUS": item.status === 0 ? "Pendente" : (item.status === 1 ? "Aprovado" : (item.status === 3 ? "Regularização solicitada" : "Rejeitado")),
            }));

            await downloadXlsx(rows, 'credenciamentos', { sheetName: 'Credenciamentos' });

            this.$dialog.notify.info("Planilha Excel gerada com sucesso!", { position: "top-right", timeout: 2000 });
        },
    },
};
</script>

<style lang="scss" scoped>
.case-name-ellipsis {
    display: block;
    max-width: 210px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
